import axios, { type AxiosError, type AxiosRequestConfig } from "axios"
import Cookies from "js-cookie"

import { env } from "./env"

export const BASE_URL = env.VITE_API_OLD_ENDPOINT.replace(/\/$/, "") // Remove trailing slash

// Axios Instance
const DataService = axios.create({
  baseURL: BASE_URL,
  timeout: 300_000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "7777777777777",
  },
})

// Request Interceptor to add CSRF token
DataService.interceptors.request.use(
  (config) => {
    const csrfToken = Cookies.get("csrf_access_token")
    if (csrfToken) {
      config.headers = { ...config.headers, "X-CSRF-TOKEN": csrfToken }
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Increase loading time
// DataService.interceptors.request.use(async (config) => {
//   await new Promise((resolve) => setTimeout(resolve, 1000000))
//   return config
// })

let isRefreshing = false
let refreshPromise: Promise<void> | null = null

async function handleRefreshToken(error: AxiosError) {
  const csrfRefreshToken = Cookies.get("csrf_refresh_token")
  if (!csrfRefreshToken) {
    return Promise.reject(error)
  }

  if (!isRefreshing) {
    isRefreshing = true
    refreshPromise = axios
      .post(
        `${BASE_URL}/autenticacao/refresh`,
        {},
        {
          withCredentials: true,
          headers: { "X-CSRF-TOKEN": csrfRefreshToken },
        }
      )
      .then(() => {
        isRefreshing = false
        refreshPromise = null
      })
      .catch(async (err) => {
        isRefreshing = false
        refreshPromise = null

        await axios
          .delete(`${BASE_URL}/autenticacao/revogar-tokens`, {
            withCredentials: true,
          })
          .catch((_err) => {})
        throw err
      })
  }

  // Wait for refresh to complete
  return refreshPromise?.then(() => {
    if (!error.config) {
      return Promise.reject(error)

      // Mark as retried
    }
    ;(error.config as any)._retry = true
    return DataService(error.config)
  })
}

// Response Interceptor to handle token expiration
DataService.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config

    if (
      error.response?.status === 401 &&
      error.response.data?.[0]?.msg === "Token expirado." &&
      !originalRequest?._retry &&
      !originalRequest?.url?.includes("/autenticacao/refresh") &&
      !originalRequest?.url?.includes("/autenticacao/revogar-tokens")
    ) {
      ;(originalRequest as any)._retry = true
      return handleRefreshToken(error)
    }

    return Promise.reject(error)
  }
)

// Generic API request function
export async function apiRequest<T>(config: AxiosRequestConfig): Promise<T> {
  return DataService(config)
    .then((response) => response.data)
    .catch((err) => {
      throw err
    })
}

export { DataService }
